import { useMemo } from 'react';
import { useAdpAnalytics } from '@innovyze/stylovyze';

const CONTEXT = 'Cost Tables';
const EVENT = 'CLICK_COMPONENT';
const NAME = 'COMMON.COMMAND_INVOKED';

export const useCostTableTracking = () => {
  const adp = useAdpAnalytics();

  return useMemo(
    () => ({
      trackCreateSuccess: (data: {
        costtableid: string;
        assetgroupsetid: string;
        costper: string;
      }) => {
        adp?.track(EVENT, {
          id: 'cost-table-create-success',
          type: 'command',
          name: NAME,
          attributes: {
            'COMMON.COMMAND_NAME': 'Cost Table Create Success',
            'COMMON.COMMAND_CONTEXT': CONTEXT,
            costtableid: data.costtableid,
            assetgroupsetid: data.assetgroupsetid,
            costper: data.costper,
          },
        });
      },

      trackCreateFailed: (data: { error: string }) => {
        adp?.track(EVENT, {
          id: 'cost-table-create-failed',
          type: 'command',
          name: NAME,
          attributes: {
            'COMMON.COMMAND_NAME': 'Cost Table Create Failed',
            'COMMON.COMMAND_CONTEXT': CONTEXT,
            error: data.error,
          },
        });
      },

      trackCopySuccess: (data: {
        sourcecosttableid: string;
        newcosttableid: string;
      }) => {
        adp?.track(EVENT, {
          id: 'cost-table-copy-success',
          type: 'command',
          name: NAME,
          attributes: {
            'COMMON.COMMAND_NAME': 'Cost Table Copy Success',
            'COMMON.COMMAND_CONTEXT': CONTEXT,
            sourcecosttableid: data.sourcecosttableid,
            newcosttableid: data.newcosttableid,
          },
        });
      },

      trackCopyFailed: (data: { sourcecosttableid: string; error: string }) => {
        adp?.track(EVENT, {
          id: 'cost-table-copy-failed',
          type: 'command',
          name: NAME,
          attributes: {
            'COMMON.COMMAND_NAME': 'Cost Table Copy Failed',
            'COMMON.COMMAND_CONTEXT': CONTEXT,
            sourcecosttableid: data.sourcecosttableid,
            error: data.error,
          },
        });
      },

      trackRenameSuccess: (data: { costtableid: string }) => {
        adp?.track(EVENT, {
          id: 'cost-table-rename-success',
          type: 'command',
          name: NAME,
          attributes: {
            'COMMON.COMMAND_NAME': 'Cost Table Rename Success',
            'COMMON.COMMAND_CONTEXT': CONTEXT,
            costtableid: data.costtableid,
          },
        });
      },

      trackRenameFailed: (data: { costtableid: string; error: string }) => {
        adp?.track(EVENT, {
          id: 'cost-table-rename-failed',
          type: 'command',
          name: NAME,
          attributes: {
            'COMMON.COMMAND_NAME': 'Cost Table Rename Failed',
            'COMMON.COMMAND_CONTEXT': CONTEXT,
            costtableid: data.costtableid,
            error: data.error,
          },
        });
      },

      trackView: (data: { costtableid: string }) => {
        adp?.track(EVENT, {
          id: 'cost-table-view',
          type: 'command',
          name: NAME,
          attributes: {
            'COMMON.COMMAND_NAME': 'Cost Table View',
            'COMMON.COMMAND_CONTEXT': CONTEXT,
            costtableid: data.costtableid,
          },
        });
      },

      trackSaveSuccess: (data: {
        costtableid: string;
        changecount: number | string;
      }) => {
        adp?.track(EVENT, {
          id: 'cost-table-save-success',
          type: 'command',
          name: NAME,
          attributes: {
            'COMMON.COMMAND_NAME': 'Cost Table Save Success',
            'COMMON.COMMAND_CONTEXT': CONTEXT,
            costtableid: data.costtableid,
            changecount: String(data.changecount),
          },
        });
      },

      trackSaveFailed: (data: { costtableid: string; error: string }) => {
        adp?.track(EVENT, {
          id: 'cost-table-save-failed',
          type: 'command',
          name: NAME,
          attributes: {
            'COMMON.COMMAND_NAME': 'Cost Table Save Failed',
            'COMMON.COMMAND_CONTEXT': CONTEXT,
            costtableid: data.costtableid,
            error: data.error,
          },
        });
      },

      trackCsvUploadStart: (data: { costtableid: string }) => {
        adp?.track(EVENT, {
          id: 'cost-table-csv-upload-start',
          type: 'command',
          name: NAME,
          attributes: {
            'COMMON.COMMAND_NAME': 'Cost Table CSV Upload Start',
            'COMMON.COMMAND_CONTEXT': CONTEXT,
            costtableid: data.costtableid,
          },
        });
      },

      trackCsvUploadComplete: (data: {
        costtableid: string;
        rowcount: number | string;
      }) => {
        adp?.track(EVENT, {
          id: 'cost-table-csv-upload-complete',
          type: 'command',
          name: NAME,
          attributes: {
            'COMMON.COMMAND_NAME': 'Cost Table CSV Upload Complete',
            'COMMON.COMMAND_CONTEXT': CONTEXT,
            costtableid: data.costtableid,
            rowcount: String(data.rowcount),
          },
        });
      },

      trackCsvUploadFailed: (data: { costtableid: string; error: string }) => {
        adp?.track(EVENT, {
          id: 'cost-table-csv-upload-failed',
          type: 'command',
          name: NAME,
          attributes: {
            'COMMON.COMMAND_NAME': 'Cost Table CSV Upload Failed',
            'COMMON.COMMAND_CONTEXT': CONTEXT,
            costtableid: data.costtableid,
            error: data.error,
          },
        });
      },

      trackAdjustUnitCost: (data: {
        costtableid: string;
        rowcount: number | string;
        adjustmentpercent: number | string;
        adjustmenttype: 'increase' | 'decrease';
      }) => {
        adp?.track(EVENT, {
          id: 'cost-table-adjust-unit-cost',
          type: 'command',
          name: NAME,
          attributes: {
            'COMMON.COMMAND_NAME': 'Cost Table Adjust Unit Cost',
            'COMMON.COMMAND_CONTEXT': CONTEXT,
            costtableid: data.costtableid,
            rowcount: String(data.rowcount),
            adjustmentpercent: String(data.adjustmentpercent),
            adjustmenttype: data.adjustmenttype,
          },
        });
      },

      trackSetUnitCost: (data: {
        costtableid: string;
        rowcount: number | string;
        value: number | string;
      }) => {
        adp?.track(EVENT, {
          id: 'cost-table-set-unit-cost',
          type: 'command',
          name: NAME,
          attributes: {
            'COMMON.COMMAND_NAME': 'Cost Table Set Unit Cost',
            'COMMON.COMMAND_CONTEXT': CONTEXT,
            costtableid: data.costtableid,
            rowcount: String(data.rowcount),
            value: String(data.value),
          },
        });
      },

      trackDeleteSuccess: (data: { costtableid: string }) => {
        adp?.track(EVENT, {
          id: 'cost-table-delete-success',
          type: 'command',
          name: NAME,
          attributes: {
            'COMMON.COMMAND_NAME': 'Cost Table Delete Success',
            'COMMON.COMMAND_CONTEXT': CONTEXT,
            costtableid: data.costtableid,
          },
        });
      },

      trackDeleteFailed: (data: { costtableid: string; error: string }) => {
        adp?.track(EVENT, {
          id: 'cost-table-delete-failed',
          type: 'command',
          name: NAME,
          attributes: {
            'COMMON.COMMAND_NAME': 'Cost Table Delete Failed',
            'COMMON.COMMAND_CONTEXT': CONTEXT,
            costtableid: data.costtableid,
            error: data.error,
          },
        });
      },

      trackExport: (data: {
        costtableid: string;
        rowcount: number | string;
      }) => {
        adp?.track(EVENT, {
          id: 'cost-table-export-initiated',
          type: 'command',
          name: NAME,
          attributes: {
            'COMMON.COMMAND_NAME': 'Cost Table Export Initiated',
            'COMMON.COMMAND_CONTEXT': CONTEXT,
            costtableid: data.costtableid,
            rowcount: String(data.rowcount),
          },
        });
      },
    }),
    // adp is the only external dependency; all tracker functions close over it
    [adp]
  );
};
